import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { SettingService } from './setting.service';
import { LoginService } from './login.service';

export interface EstadoResponse {
  numeroError: number;
  mensajeError: string;
}

export interface ParametroSistema {
  sCodParametro01: string;
  sDesLargo01: string;
  sDesLargo02: string;
  sDesLargo03: string;
  sDesLargo04: string;
  sIndActivo: string;
}

export interface BuscarParametrosResponse {
  estado: EstadoResponse;
  parametros: ParametroSistema[];
}

@Injectable({ providedIn: 'root' })
export class ParametrosService {
  private baseUrl: string;

  // cache sincrónico para consumo desde pipes/templates (ver MonedaPipe)
  signoMoneda = '$';
  monedaDecimales = 2;
  private monedaCargada = false;

  constructor(
    private http: HttpClient,
    private settings: SettingService,
    private loginService: LoginService
  ) {
    this.baseUrl = this.settings.settings.admService; // ejemplo: http://localhost:8080/RCAEBack
    this.cargarMoneda();
  }

  private cargarMoneda() {
    if (this.monedaCargada) return;
    this.monedaCargada = true;

    this.obtenerMoneda().subscribe({
      next: ({ estado, parametros }) => {
        if (estado?.numeroError === 0 && parametros?.[0]) {
          this.signoMoneda = parametros[0].sDesLargo04 || this.signoMoneda;
          const decimales = Number(parametros[0].sDesLargo03);
          if (!isNaN(decimales)) this.monedaDecimales = decimales;
        }
      },
      error: () => { /* se mantienen los valores por defecto */ }
    });
  }

  async fetchParametros(nNumParametro: number) {
    const usuario = this.loginService.getUsuarioPayload?.();
    const idEmpresa = Number(localStorage.getItem('idEmpresa')) || 0;
    if (!usuario || !usuario.sKsi) {
      return { ok: false, message: 'Usuario no autenticado' };
    }

    const body = {
      nNumParametro: nNumParametro,
      nIdEmpresaFiltro: idEmpresa // Se le entrega de forma dinámica desde el localStorage de login.component.ts
    };

    try {
      const url = `${this.baseUrl}/AdmBackend/parametro/buscarParametrosSistema`;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      
      // 👇 aquí va el .toPromise()
      const response = await this.http.post(url, body, { headers }).toPromise();

      return { ok: true, data: response };
    } catch (err) {
      return { ok: false, message: 'Error al consultar parámetro', error: err };
    }
  }

  obtenerMoneda(): Observable<BuscarParametrosResponse> {
    // getUsuarioPayload() depende de claves de localStorage ('sesIdUsuario', 'sesIdsesion')
    // que el login real nunca escribe (ver login.component.ts), por lo que siempre da null.
    // Se arma el payload con las mismas claves que sí usan aside/movimientos ('KSI', 'sesionPocae', 'idEmpresa').
    const sesionPocae = JSON.parse(localStorage.getItem('sesionPocae') || '{}');
    const ksi = localStorage.getItem('KSI') || sesionPocae?.ksi || '';
    const idUsuario = Number(localStorage.getItem('idUsuario') || sesionPocae?.idUsuario || 0);
    const idEmpresa = Number(localStorage.getItem('idEmpresa')) || 0;

    if (!ksi) {
      return throwError(() => ({ message: 'Usuario no autenticado' }));
    }

    const usuario = {
      nIdEmpresa: idEmpresa,
      nNumUsuario: idUsuario,
      sKsi: ksi,
      sHost: '127.0.0.1'
    };

    const body = {
      usuario: usuario,
      nNumParametro: 20,
      sCodParametro01: '2',
      nIdEmpresaFiltro: idEmpresa
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<BuscarParametrosResponse>(
      `${this.baseUrl}/AdmBackend/parametro/buscarParametrosSistema`,
      body,
      { headers }
    ).pipe(
      retry(4),
      catchError(err => throwError(() => err))
    );
  }
}
